/**
 * RelationalBlueprintPage — dyadic strategic Blueprint generation surface
 * with Power Asymmetry forced render + Flip mode (vantage swap).
 */

import { useState } from "react";
import { useSession } from "../lib/useSession.js";
import {
  Wordmark, SectionHeader, Callout, SeriesBanner, AuditFooter, MarkdownV2, Chip, PrintToPdfButton, RecentBlueprintsPanel,
} from "../components/index.js";
import MentionAutocomplete from "../components/MentionAutocomplete.jsx";
import BlueprintChat from "../components/BlueprintChat.jsx";
import StreamingMarkdown from "../components/StreamingMarkdown.jsx";
import { useStreamingBlueprint } from "../lib/use-streaming-blueprint.js";
import { terminalActionButtonStyle, terminalActionButtonDisabledStyle } from "../lib/buttonStyles.js";

const RELATIONAL_PRESETS = [
  { label: "Draft my opening message",                       preset_text: "Draft my opening message to this person for this situation." },
  { label: "What if they say [X]?",                          preset_text: "If they push back, what's the response that lands for their behavioral profile?" },
  { label: "Smallest move that changes the trajectory",      preset_text: "What's the smallest move I could make this week that probably changes the trajectory of this dynamic?" },
  { label: "Show me this from their side",                   preset_text: "Show me this dynamic from the other person's side — what move of mine looks worst from their vantage?" },
  { label: "If this doesn't get better in 30 days",          preset_text: "If this dynamic doesn't get better in 30 days, what's the path forward?" },
  { label: "What's the move that resets if I screw up?",     preset_text: "What's the move that resets this dynamic if I screw up the first attempt?" },
];

const SITUATIONS = [
  { value: "ongoing", label: "Ongoing relationship" },
  { value: "conflict", label: "Conflict" },
  { value: "negotiation", label: "Negotiation" },
  { value: "new_counterpart", label: "New counterpart / first contact" },
  { value: "partnership", label: "Partner / channel relationship" },
  { value: "selling_above", label: "Selling above your contact" },
  { value: "hard_news", label: "Hard conversation / bad news" },
  { value: "expansion", label: "Expansion / upsell" },
  { value: "repair", label: "Repair after a rupture" },
  { value: "client_kickoff", label: "New client kickoff" },
  { value: "decision_maker_change", label: "Decision-maker change" },
  { value: "advisory", label: "Advisory / coaching relationship" },
];

export default function RelationalBlueprintPage() {
  const { session, appUser } = useSession();
  const [personAMentions, setPersonAMentions] = useState([]);
  const [personAText, setPersonAText] = useState("");
  const [personBMentions, setPersonBMentions] = useState([]);
  const [personBText, setPersonBText] = useState("");
  const [situation, setSituation] = useState("ongoing");
  const [history, setHistory] = useState("");
  const [desiredOutcome, setDesiredOutcome] = useState("");
  const [flipMode, setFlipMode] = useState(false);
  const [localError, setLocalError] = useState(null);

  const stream = useStreamingBlueprint({
    syncUrl: "/.netlify/functions/relational-blueprint",
    streamUrl: "/api/relational-blueprint/stream",
    bearerToken: session?.access_token,
  });
  const { streaming, setStreaming, generating, streamRequest, blueprint, error, submit, submitSync, onStreamDone } = stream;

  const isHrOnBehalfOf = (appUser?.is_hr_admin || appUser?.tier === "hr_director") && personAMentions.length > 0;

  const buildPayload = (overrideFlip) => ({
    person_a_profile_id: personAMentions[0]?.profile_id || null,
    person_b_profile_id: personBMentions[0]?.profile_id,
    situation_type: situation,
    history,
    desired_outcome: desiredOutcome,
    flip_mode: overrideFlip ?? flipMode,
  });

  const generate = async () => {
    if (personBMentions.length === 0) {
      setLocalError("Mention Person B with @ to generate the Blueprint.");
      return;
    }
    setLocalError(null);
    submit(buildPayload());
  };

  // Flip regenerations always go sync — streaming would unmount the article.
  const flip = async () => {
    const next = !flipMode;
    const result = await submitSync(buildPayload(next));
    if (result?.ok) setFlipMode(next);
  };

  if (!session) return <p style={{ padding: 24 }}>Sign in to generate a Relational Blueprint.</p>;

  return (
    <div style={{ maxWidth: 980, margin: "0 auto", padding: "32px 24px" }}>
      <div style={{ marginBottom: 24 }}>
        <Wordmark scale={1.0} />
      </div>

      {streamRequest && !blueprint ? (
        <StreamingMarkdown
          url={stream.streamUrl}
          bearerToken={session.access_token}
          body={streamRequest}
          onDone={onStreamDone}
          onRetry={stream.retry}
        />
      ) : !blueprint ? (
        <div>
          <RecentBlueprintsPanel
            type="relational"
            bearerToken={session.access_token}
          />
          <SectionHeader index={1} title="New Relational Blueprint" />

          <Callout.Tip>
            The broadest dyadic surface in Interaction Engineering. Translation table goes both directions. Power Asymmetry surfaces automatically when one side holds structural power.
          </Callout.Tip>

          <div style={{ display: "grid", gap: 16, marginTop: 20 }}>
            {(appUser?.is_hr_admin || appUser?.tier === "hr_director") && (
              <Field label="Person A (on behalf of someone else — leave empty to use yourself)">
                <MentionAutocomplete
                  value={personAText}
                  onChange={setPersonAText}
                  mentions={personAMentions}
                  onMentionsChange={setPersonAMentions}
                  placeholder="@Sarah (defaults to you)"
                  rows={2}
                  bearerToken={session.access_token}
                />
              </Field>
            )}

            <Field label="Person B (mention with @)">
              <MentionAutocomplete
                value={personBText}
                onChange={setPersonBText}
                mentions={personBMentions}
                onMentionsChange={setPersonBMentions}
                placeholder="@Marcus"
                rows={2}
                bearerToken={session.access_token}
              />
            </Field>

            <Field label="Situation type">
              <select
                value={situation}
                onChange={(e) => setSituation(e.target.value)}
                style={{ ...inputStyle, cursor: "pointer" }}
              >
                {SITUATIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </Field>

            <Field label="History (optional, high-value)">
              <textarea
                value={history}
                onChange={(e) => setHistory(e.target.value)}
                placeholder="Recent emails, Slack threads, prior 1:1 notes, the inciting incident…"
                rows={4}
                style={inputStyle}
              />
            </Field>

            <Field label="What you want from this (optional)">
              <input
                value={desiredOutcome}
                onChange={(e) => setDesiredOutcome(e.target.value)}
                placeholder="I want to fix the working relationship."
                style={inputStyle}
              />
            </Field>

            {isHrOnBehalfOf && (
              <Callout.WatchOut>
                Generating on behalf of another user. Both Person A and Person B will receive a redacted notification that a Pro account generated a relational analysis involving them.
              </Callout.WatchOut>
            )}

            {(localError || error) && <ErrorBox error={localError || error} />}

            <button
              onClick={() => generate()}
              disabled={generating}
              style={generating ? terminalActionButtonDisabledStyle : terminalActionButtonStyle}
            >
              {generating ? "Generating Blueprint…" : "Generate Blueprint"}
            </button>
          </div>
        </div>
      ) : (
        <RenderedBlueprint
          blueprint={blueprint}
          flipMode={flipMode}
          onFlip={flip}
          flipping={generating}
          bearerToken={session.access_token}
          subjectFirstNames={[
            ...personAMentions.map((m) => m.display_name),
            ...personBMentions.map((m) => m.display_name),
          ].filter(Boolean)}
        />
      )}
    </div>
  );
}

function RenderedBlueprint({ blueprint, flipMode, onFlip, flipping, bearerToken, subjectFirstNames }) {
  return (
    <article>
      <SeriesBanner
        type={`Relational Blueprint · ${blueprint.situation_type}${flipMode ? " · Flipped" : ""}`}
        dateLabel={new Date().toISOString().slice(0, 10)}
      />

      <div style={{ marginTop: 28 }}>
        <h1 style={titleStyle}>{blueprint.title}</h1>
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 8 }}>
          {blueprint.power_asymmetry_rendered && (
            <span style={{
              padding: "4px 10px",
              background: "var(--ppr-color-accent-coral-very-soft)",
              color: "var(--ppr-color-accent-coral-dark)",
              fontFamily: "var(--ppr-typography-font-family-display)",
              fontSize: "11px",
              letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
              textTransform: "uppercase",
            }}>
              Structural power: {blueprint.power_direction === "a_to_b" ? "A → B" : blueprint.power_direction === "b_to_a" ? "B → A" : "—"}
            </span>
          )}
          <button onClick={onFlip} disabled={flipping} style={secondaryButton}>
            {flipping ? "Flipping…" : flipMode ? "↩ Un-flip" : "↻ Flip — show this from their side"}
          </button>
        </div>
      </div>

      {blueprint.power_asymmetry_rendered && (
        <Callout.WatchOut>
          {flipMode
            ? "Flipped view — vantage is the other side, but structural power direction has not flipped. This view is for empathy, not for leverage."
            : "You hold structural power in this relationship. Use the translation table to translate, not to maneuver. Misuse is the kind of thing the consistency audit is designed to surface."}
        </Callout.WatchOut>
      )}

      {/* MarkdownV2 with subject-name auto-bold (#80). subjectFirstNames
          is threaded in from the parent (form-state mentions aren't in
          scope inside this sub-component). */}
      <MarkdownV2
        source={blueprint.blueprint_markdown}
        subjectNames={subjectFirstNames || []}
      />

      {blueprint.unlock_headline && (
        <Callout.KeyInsight>
          <strong>Unlock move:</strong> {blueprint.unlock_headline}
        </Callout.KeyInsight>
      )}

      <BlueprintChat
        blueprintId={blueprint.blueprint_id}
        blueprintType="relational"
        presetPrompts={RELATIONAL_PRESETS}
        bearerToken={bearerToken}
      />

      <AuditFooter
        engineVersion={blueprint.engine_version}
        jargonScanPassed={true}
        blueprintId={blueprint.blueprint_id}
      />

      {/* Print-to-PDF (#73). */}
      <div style={{ marginTop: 24, display: "flex", justifyContent: "flex-end" }}>
        <PrintToPdfButton label="Save as PDF" />
      </div>
    </article>
  );
}

const inputStyle = {
  padding: "12px 14px",
  fontFamily: "var(--ppr-typography-font-family-ui)",
  fontSize: "var(--ppr-typography-font-size-base)",
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-background-surface)",
  color: "var(--ppr-color-text-primary)",
  width: "100%",
  outline: "none",
};

const secondaryButton = {
  padding: "8px 14px",
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: "var(--ppr-typography-font-size-sm)",
  letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
  textTransform: "uppercase",
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-background-surface)",
  color: "var(--ppr-color-accent-navy)",
  cursor: "pointer",
};

const titleStyle = {
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: "var(--ppr-typography-font-size-4xl)",
  color: "var(--ppr-color-accent-navy)",
  letterSpacing: "var(--ppr-typography-letter-spacing-tight)",
  lineHeight: "var(--ppr-typography-line-height-tight)",
  margin: "0 0 8px",
};

function Field({ label, children }) {
  return (
    <div>
      <label style={{
        display: "block",
        fontFamily: "var(--ppr-typography-font-family-display)",
        fontSize: "var(--ppr-typography-font-size-sm)",
        letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
        textTransform: "uppercase",
        color: "var(--ppr-color-text-secondary)",
        marginBottom: 6,
      }}>{label}</label>
      {children}
    </div>
  );
}

function ErrorBox({ error }) {
  return (
    <div style={{
      padding: "12px 14px",
      border: "2px solid var(--ppr-color-accent-coral)",
      background: "var(--ppr-color-accent-coral-very-soft)",
      color: "var(--ppr-color-accent-coral-dark)",
    }}>
      {error}
    </div>
  );
}
